import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, TrendingUp, DollarSign } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Breadcrumbs } from '@/components/Breadcrumbs';

/**
 * Story 83.3 — Multi-tenant agency dashboard.
 *
 * Shows all tenants managed by the logged-in AGENCY_OWNER with health
 * metrics (active users, deals closed last 30d, open pipeline value).
 * Clicking a tenant row switches tenant context via normal re-login
 * (the JWT carries the active tenant_id).
 */

interface TenantHealth {
  tenantId: string;
  tenantName: string;
  activeUsers: number;
  dealsClosedLast30d: number;
  openPipelineValue: number;
}

export function AgencyPortfolioPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<TenantHealth[]>('/agency/portfolio')
      .then(({ data }) => setTenants(data))
      .catch(() => setError(t('agency.load_error')))
      .finally(() => setLoading(false));
  }, [t]);

  return (
    <div className="p-6 space-y-6">
      <Breadcrumbs
        crumbs={[{ label: t('agency.breadcrumb') }]}
      />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('agency.title')}
        </h1>
      </div>

      {loading && (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      )}

      {error && (
        <p className="text-destructive">{error}</p>
      )}

      {!loading && !error && tenants.length === 0 && (
        <Card className="p-8 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t('agency.empty')}</p>
        </Card>
      )}

      {!loading && tenants.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {tenants.map((tenant) => (
            <TenantHealthCard
              key={tenant.tenantId}
              tenant={tenant}
              onSelect={() => navigate(`/accounts?tenantId=${tenant.tenantId}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TenantHealthCard({
  tenant,
  onSelect,
}: {
  tenant: TenantHealth;
  onSelect: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card className="p-5 space-y-4 hover:border-primary/50 transition-colors">
      <div className="flex items-center gap-3">
        <Building2 className="h-8 w-8 text-primary flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground truncate">
            {tenant.tenantName}
          </h3>
          <p className="text-xs text-muted-foreground truncate">
            {tenant.tenantId}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MetricTile
          icon={<Users className="h-4 w-4" />}
          label={t('agency.active_users')}
          value={String(tenant.activeUsers)}
        />
        <MetricTile
          icon={<TrendingUp className="h-4 w-4" />}
          label={t('agency.deals_30d')}
          value={String(tenant.dealsClosedLast30d)}
        />
        <MetricTile
          icon={<DollarSign className="h-4 w-4" />}
          label={t('agency.pipeline')}
          value={formatCurrency(tenant.openPipelineValue)}
        />
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={onSelect}
      >
        {t('agency.view_tenant')}
      </Button>
    </Card>
  );
}

function MetricTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1 p-2 rounded-md bg-muted/40">
      <div className="text-muted-foreground">{icon}</div>
      <p className="text-lg font-semibold text-foreground leading-tight">
        {value}
      </p>
      <p className="text-[10px] text-muted-foreground text-center leading-tight">
        {label}
      </p>
    </div>
  );
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
